import { useState, useEffect } from 'react';
import axios from 'axios';
import type { PublicMaintenanceWindow } from '@askporter/client-grieg-lyric';

export type PlatformFlags = Record<string, boolean>;

/**
 * The declared maintenance window for this environment, when one exists.
 *
 * Served whether or not the window has started, so the UI can warn users ahead
 * of a scheduled start rather than discovering the window when writes begin to
 * fail (NS-2465). Deliberately narrower than the internal record: the
 * operator's `reason` is never served, being internal-only under the
 * maintenance-mode contract.
 */
export type MaintenanceWindow = PublicMaintenanceWindow & {
  /**
   * How many minutes before `scheduledStart` the operator wants users warned,
   * where they declared something other than this client's default.
   *
   * Widened onto the published type rather than waiting for it: the field lands
   * in `@askporter/client-grieg-lyric` with spec 0.143.0 (sdk#934), and pinning
   * an unpublished version here would break the install for the whole
   * workspace. **Delete this widening when the client pin next moves** — the
   * generated type will carry it.
   */
  warningLeadMinutes?: number;
};

/** Shape of the `/api/v1/static/flags` response body. */
interface PlatformFlagsResponse {
  flags?: PlatformFlags;
  maintenanceWindow?: MaintenanceWindow;
}

/** Grieg's unauthenticated, no-cache release-flags endpoint (COM-111). */
export const PLATFORM_FLAGS_ENDPOINT = '/api/v1/static/flags';

const DEFAULT_POLL_INTERVAL_MS = 60_000;

/**
 * One poll, shared by every consumer.
 *
 * Each hook below used to own a `useState` + `setInterval` + `axios.get`, so
 * the endpoint was hit once per mounted consumer per interval — four times a
 * minute before this ticket added a fifth. They also drifted out of step with
 * each other, so the banner and the takeover could disagree about whether a
 * window was open for up to a full interval.
 *
 * The fetch now lives here and the hooks subscribe, which collapses that to a
 * single request and guarantees every consumer sees the same snapshot. The
 * pattern mirrors the refusal store in `./maintenance`.
 */
/** `settled` turns true once the first poll has finished, whether it succeeded or failed. */
type PlatformFlagsSnapshot = PlatformFlagsResponse & { settled?: boolean };

let snapshot: PlatformFlagsSnapshot = {};
const subscribers = new Set<(next: PlatformFlagsSnapshot) => void>();
let timer: number | undefined;
let subscriberIntervalMs = DEFAULT_POLL_INTERVAL_MS;

const publish = (next: PlatformFlagsSnapshot) => {
  snapshot = next;
  subscribers.forEach((notify) => notify(snapshot));
};

const markSettled = () => {
  if (!snapshot.settled) publish({ ...snapshot, settled: true });
};

const fetchFlags = async () => {
  try {
    const { data } = await axios.get<PlatformFlagsResponse>(PLATFORM_FLAGS_ENDPOINT);
    if (!data) {
      markSettled();
      return;
    }

    // A response is only allowed to *replace* the snapshot, never half-update
    // it: `maintenanceWindow` is absent when no window is declared, so merging
    // would strand a cancelled window on screen for the life of the page.
    publish({ flags: data.flags ?? {}, maintenanceWindow: data.maintenanceWindow, settled: true });
  } catch {
    // Keep the last-known snapshot on error — never surface a failure to the
    // UI, mirroring henry-rollins' default-false resilience.
    markSettled();
  }
};

const startPolling = (pollIntervalMs: number) => {
  subscriberIntervalMs = pollIntervalMs;
  void fetchFlags();
  timer = window.setInterval((): void => {
    void fetchFlags();
  }, subscriberIntervalMs);
};

const subscribe = (notify: (next: PlatformFlagsSnapshot) => void, pollIntervalMs: number): (() => void) => {
  subscribers.add(notify);
  if (subscribers.size === 1) startPolling(pollIntervalMs);

  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0 && timer !== undefined) {
      window.clearInterval(timer);
      timer = undefined;
    }
  };
};

/** Resets the shared poll. Test-only — production code has no reason to call it. */
export const __resetPlatformFlagsPoll = (): void => {
  if (timer !== undefined) window.clearInterval(timer);
  timer = undefined;
  subscribers.clear();
  snapshot = {};
};

/**
 * Subscribes to the shared poll and returns the whole snapshot.
 *
 * The first consumer to mount starts the poll; the last to unmount stops it.
 * The interval is taken from that first consumer, so passing a different one
 * from a later hook has no effect — deliberate, since a shared poll can only
 * have one period and the alternative is a silent race over which wins.
 */
const usePlatformFlagsSnapshot = (pollIntervalMs: number): PlatformFlagsSnapshot => {
  const [current, setCurrent] = useState<PlatformFlagsSnapshot>(snapshot);

  useEffect(() => subscribe(setCurrent, pollIntervalMs), [pollIntervalMs]);

  return current;
};

/**
 * Polls Grieg's `/api/v1/static/flags` endpoint for the resolved release-flags
 * map. Grieg serves this uncached, sourced from henry-rollins, so a flag flip
 * reaches the browser within roughly the poll interval (~1 minute by default).
 *
 * Never throws — on any error the last-known flags are retained (empty on a
 * cold failure), mirroring henry-rollins' default-false resilience.
 *
 * @param pollIntervalMs - How often to re-fetch the flags, in milliseconds.
 * @returns The current resolved flags map (empty until the first fetch lands).
 */
export function usePlatformFlags(pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS): PlatformFlags {
  return usePlatformFlagsSnapshot(pollIntervalMs).flags ?? {};
}

/**
 * The declared maintenance window, from the same poll as {@link usePlatformFlags}.
 *
 * Returns undefined when no window is declared, which is also what a cancelled
 * window looks like on the next poll — so a warning built on this clears itself
 * without a reload (NS-2465).
 *
 * @param pollIntervalMs - How often to re-fetch, in milliseconds.
 * @returns The declared window, or undefined when there is none.
 */
export function useMaintenanceWindow(pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS): MaintenanceWindow | undefined {
  return usePlatformFlagsSnapshot(pollIntervalMs).maintenanceWindow;
}

/**
 * Whether the first poll has finished, successfully or not.
 *
 * An empty flags map is ambiguous until then: it can mean "every flag off" or
 * "not fetched yet", and a flag-gated route must not 404 on the latter.
 *
 * @param pollIntervalMs - How often to re-fetch, in milliseconds.
 * @returns True once the first poll has settled.
 */
export function usePlatformFlagsSettled(pollIntervalMs: number = DEFAULT_POLL_INTERVAL_MS): boolean {
  return usePlatformFlagsSnapshot(pollIntervalMs).settled === true;
}
